import { LoadingState } from '@/entities/LoadingState';
import { MutationTree, ActionTree, GetterTree, Module, ActionContext } from 'vuex';
import { RootState } from '../rootState';
import removeNamespace from '../removeNamespace';
import { ShoppingCartUploadService } from '@/services/shopping-cart-upload.service';
import { ShoppingCartUploadInfo } from '@/entities/shopping-cart-upload/ShoppingCartUploadInfo';
import { ShoppingCartUploadProcessingState } from '@/entities/shopping-cart-upload/ShoppingCartUploadProcessingState';
import { PartService } from '@/services/part-service';
import { ShoppingCartUploadResultType } from '@/entities/shopping-cart-upload/ShoppingCartUploadResultType';
import { PartAvailability } from '@/entities/PartAvailability';

const shoppingCartUploadService = new ShoppingCartUploadService();
const partService = new PartService();

const namespace = 'shoppingCartUpload';

export const ShoppingCartUploadModuleTypes = {
    getters: {
        loadingState: `${namespace}/loadingState`,
        uploadInfo: `${namespace}/uploadInfo`,
        selectedPartIds: `${namespace}/selectedPartIds`,
        itemsThatAreLoadingAvailability: `${namespace}/itemsThatAreLoadingAvailability`,
    },
    mutations: {
        updateLoadingState: `${namespace}/updateLoadingState`,
        uploadFileAndStartSearch: `${namespace}/uploadFileAndStartSearch`,
        uploadFileAndStartSearchFail: `${namespace}/uploadFileAndStartSearchFail`,
        setSelectedPartIds: `${namespace}/setSelectedPartIds`,
        selectPartWithId: `${namespace}/selectPartWithId`,
        deselectPartWithId: `${namespace}/deselectPartWithId`,
        updateCurrentUploadInfo: `${namespace}/updateCurrentUploadInfo`,
        reloadAvailabilityOfShoppingCartUploadItem: `${namespace}/reloadAvailabilityOfShoppingCartUploadItem`,
        reloadAvailabilityOfShoppingCartUploadItemSuccess: `${namespace}/reloadAvailabilityOfShoppingCartUploadItemSuccess`,
        reloadAvailabilityOfShoppingCartUploadItemFail: `${namespace}/reloadAvailabilityOfShoppingCartUploadItemFail`,
    },
    actions: {
        uploadFileAndStartSearch: `${namespace}/uploadFileAndStartSearch`,
        updateCurrentUploadInfo: `${namespace}/updateCurrentUploadInfo`,
        reloadAvailabilityOfShoppingCartUploadItem: `${namespace}/reloadAvailabilityOfShoppingCartUploadItem`,
        updateQuantityOfShoppingCartUploadItem: `${namespace}/updateQuantityOfShoppingCartUploadItem`,
    },
};

const types = removeNamespace(namespace, ShoppingCartUploadModuleTypes);
export interface ShoppingCartUploadState {
    loadingState: LoadingState;
    uploadInfo?: ShoppingCartUploadInfo;
    selectedPartIds: string[];
    itemsThatAreLoadingAvailability: string[];
}

const state: ShoppingCartUploadState = {
    loadingState: LoadingState.Loading,
    uploadInfo: undefined,
    selectedPartIds: [],
    itemsThatAreLoadingAvailability: [],
};

const mutations: MutationTree<ShoppingCartUploadState> = {
    [types.mutations.updateLoadingState](
        currentState: ShoppingCartUploadState,
        payload: { loadingState: LoadingState }
    ): void {
        currentState.loadingState = payload.loadingState;
    },
    [types.mutations.uploadFileAndStartSearch](currentState: ShoppingCartUploadState): void {
        currentState.loadingState = LoadingState.Loading;
        currentState.uploadInfo = undefined;
    },
    [types.mutations.uploadFileAndStartSearchFail](currentState: ShoppingCartUploadState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.setSelectedPartIds](currentState: ShoppingCartUploadState, payload: { partIds: string[] }): void {
        currentState.selectedPartIds = payload.partIds;
    },
    [types.mutations.selectPartWithId](currentState: ShoppingCartUploadState, payload: { partId: string }): void {
        currentState.selectedPartIds = [...currentState.selectedPartIds, payload.partId];
    },
    [types.mutations.deselectPartWithId](currentState: ShoppingCartUploadState, payload: { partId: string }): void {
        currentState.selectedPartIds = currentState.selectedPartIds.filter((id) => id !== payload.partId);
    },
    [types.mutations.updateCurrentUploadInfo](
        currentState: ShoppingCartUploadState,
        payload: { info: ShoppingCartUploadInfo }
    ): void {
        currentState.uploadInfo = payload.info;
        if (payload.info.state !== ShoppingCartUploadProcessingState.Running) {
            currentState.loadingState = LoadingState.Loaded;
        }
    },
    [types.mutations.reloadAvailabilityOfShoppingCartUploadItem](
        currentState: ShoppingCartUploadState,
        payload: { partId: string }
    ): void {
        currentState.itemsThatAreLoadingAvailability.push(payload.partId);
    },
    [types.mutations.reloadAvailabilityOfShoppingCartUploadItemSuccess](
        currentState: ShoppingCartUploadState,
        payload: { partId: string; type: ShoppingCartUploadResultType; availability: PartAvailability }
    ): void {
        currentState.itemsThatAreLoadingAvailability = currentState.itemsThatAreLoadingAvailability.filter(
            (id) => id !== payload.partId
        );

        if (!currentState.uploadInfo || !currentState.uploadInfo.result) {
            return;
        }

        let item = null;
        switch (payload.type) {
            case ShoppingCartUploadResultType.Found:
                item = currentState.uploadInfo.result.foundParts.find((part) => part.partId === payload.partId);
                break;
            case ShoppingCartUploadResultType.Replacement:
                item = currentState.uploadInfo.result.replacementParts.find((part) => part.partId === payload.partId);
                break;
            case ShoppingCartUploadResultType.Used:
                item = currentState.uploadInfo.result.usedParts.find((part) => part.partId === payload.partId);
                break;
        }

        if (!item) {
            return;
        }

        item.partAvailability = payload.availability;
    },
    [types.mutations.reloadAvailabilityOfShoppingCartUploadItemFail](
        currentState: ShoppingCartUploadState,
        payload: { partId: string }
    ): void {
        currentState.itemsThatAreLoadingAvailability = currentState.itemsThatAreLoadingAvailability.filter(
            (id) => id !== payload.partId
        );
    },
};

const actions: ActionTree<ShoppingCartUploadState, RootState> = {
    async [types.actions.uploadFileAndStartSearch](
        context: ActionContext<ShoppingCartUploadState, RootState>,
        payload: { file: File; includeUsedParts: boolean }
    ): Promise<void> {
        context.commit(types.mutations.uploadFileAndStartSearch);
        try {
            await shoppingCartUploadService.processFile(payload.file, payload.includeUsedParts);
        } catch (error) {
            context.commit(types.mutations.uploadFileAndStartSearchFail);
        }
    },
    async [types.actions.updateCurrentUploadInfo](
        context: ActionContext<ShoppingCartUploadState, RootState>
    ): Promise<void> {
        const info = await shoppingCartUploadService.getCurrentUploadState();
        context.commit(types.mutations.updateCurrentUploadInfo, { info });
        if (info.result) {
            const partIds = info.result.foundParts.map((foundPart) => foundPart.partId);
            context.commit(types.mutations.setSelectedPartIds, { partIds });
        }
    },
    async [types.actions.reloadAvailabilityOfShoppingCartUploadItem](
        context: ActionContext<ShoppingCartUploadState, RootState>,
        payload: { partId: string; quantity: number; type: ShoppingCartUploadResultType }
    ): Promise<void> {
        context.commit(types.mutations.reloadAvailabilityOfShoppingCartUploadItem, {
            partId: payload.partId,
        });
        try {
            const availability = await partService.getAvailabilityOfPart(payload.partId, payload.quantity);
            context.commit(types.mutations.reloadAvailabilityOfShoppingCartUploadItemSuccess, {
                partId: payload.partId,
                type: payload.type,
                availability,
            });
        } catch (error) {
            context.commit(types.mutations.reloadAvailabilityOfShoppingCartUploadItemFail, {
                partId: payload.partId,
            });
        }
        return;
    },
    async [types.actions.updateQuantityOfShoppingCartUploadItem](
        context: ActionContext<ShoppingCartUploadState, RootState>,
        payload: { partId: string; quantity: number }
    ): Promise<void> {
        await shoppingCartUploadService.updateItem(payload.partId, payload.quantity);
    },
};

const getters: GetterTree<ShoppingCartUploadState, RootState> = {
    [types.getters.loadingState]: (currentState) => currentState.loadingState,
    [types.getters.selectedPartIds]: (currentState) => currentState.selectedPartIds,
    [types.getters.uploadInfo]: (currentState) => currentState.uploadInfo,
    [types.getters.itemsThatAreLoadingAvailability]: (currentState) => currentState.itemsThatAreLoadingAvailability,
};

const module: Module<ShoppingCartUploadState, RootState> = {
    state,
    mutations,
    actions,
    getters,
    namespaced: true,
};

export default module;
